import type { ReactNode } from 'react';
import { Button } from './ui/button';
import { Badge, type BadgeProps } from './ui/badge';
import { cn } from '../lib/utils';

export interface AlertRowProps {
  /** Short category/severity chip (e.g. "Critical", "NDRE"). */
  badge: ReactNode;
  /** Visual variant for the badge (controls color). */
  badgeVariant?: BadgeProps['variant'];
  /** Inline styles for the badge — used when severity color is dynamic (e.g. satellite indices). */
  badgeStyle?: React.CSSProperties;
  title: ReactNode;
  meta?: ReactNode;
  onAcknowledge?: () => void | Promise<void>;
  acknowledging?: boolean;
  className?: string;
}

export function AlertRow({
  badge,
  badgeVariant = 'default',
  badgeStyle,
  title,
  meta,
  onAcknowledge,
  acknowledging = false,
  className,
}: AlertRowProps) {
  return (
    <li
      className={cn(
        'flex flex-wrap items-start gap-3 border-b border-border py-3 last:border-0',
        className
      )}
    >
      <Badge variant={badgeVariant} style={badgeStyle} className="shrink-0">
        {badge}
      </Badge>
      <div className="min-w-[200px] flex-1">
        <div className="text-sm font-semibold text-fg">{title}</div>
        {meta && <div className="mt-0.5 text-xs text-fg-secondary">{meta}</div>}
      </div>
      {onAcknowledge && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => void onAcknowledge()}
          disabled={acknowledging}
        >
          {acknowledging ? 'Acking…' : 'Acknowledge'}
        </Button>
      )}
    </li>
  );
}
